import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";

import { useProxifiedUrl } from "@/hooks/use_proxified_url";
import { ProxifiedUrlsCache } from "@/utils";

import { useStyles } from "./image_proxified.css";
import { IImageProxifiedProps } from "./image_proxified.types";

export const ImageProxifiedWithoutCache = (props: IImageProxifiedProps) => {
  const { alwaysVisible, forceProxy, ...imgProps } = props;
  const classes = useStyles();
  const imgRef = useRef<null | HTMLImageElement>(null);
  const [hidden, setHidden] = useState(true);
  const [proxyActive, setProxyActive] = useState(false);

  const shouldUseProxy = proxyActive || !!forceProxy;
  const shouldHideImage = hidden && !alwaysVisible && !forceProxy;

  const src = useProxifiedUrl(imgProps.src, shouldUseProxy);
  const handleError = useCallback(
    (error) => {
      if (shouldUseProxy) {
        return imgProps.onError && imgProps.onError.call(imgRef.current, error);
      }

      setProxyActive(true);
    },
    [imgProps.onError, shouldUseProxy],
  );

  const handleLoad = useCallback(
    (event) => {
      imgProps.onLoad && imgProps.onLoad.call(imgRef.current, event);
      if (imgProps.src) {
        ProxifiedUrlsCache.add(imgProps.src, src);
      }
      setHidden(false);
    },
    [imgProps.onLoad, imgProps.src, src],
  );

  const combinedProps = useMemo(
    () => ({
      ...imgProps,
      className: clsx(
        {
          [classes.hidden]: shouldHideImage,
        },
        imgProps.className,
      ),
      onError: handleError,
      onLoad: handleLoad,
      src,
    }),
    [classes.hidden, handleError, handleLoad, imgProps, shouldHideImage, src],
  );

  return useMemo(
    () => <img {...combinedProps} alt={imgProps.alt} ref={imgRef} />,
    [combinedProps, imgProps.alt],
  );
};
